import { ArrowRight, FileText, Database } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import KnowledgeBaseMoodVisual from "@/components/landing/KnowledgeBaseMoodVisual";
import { useLanguage } from "@/hooks/useLanguage";

const WissensbasisFundamentSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, localizePath } = useLanguage();

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-center mb-8">
                <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('aiAgentPlattform.wissensbasis.badge')}</Badge>
              
                <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                  <span className="text-foreground">{t('aiAgentPlattform.wissensbasis.titleLine1')}</span>
                  <br />
                  <span className="text-foreground">{t('aiAgentPlattform.wissensbasis.titleLine2')}</span>
                </h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                {t('aiAgentPlattform.wissensbasis.intro')}
              </p>

              <div className="space-y-4 mb-8">
                <div 
                  className={cn(
                    "bg-white rounded-2xl p-6 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all",
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                  )}
                  style={{ transitionDelay: isVisible ? "200ms" : "0ms", transitionDuration: "600ms" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-chathero-mint/15 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-chathero-mint" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-1">{t('aiAgentPlattform.wissensbasis.contentTitle')}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{t('aiAgentPlattform.wissensbasis.contentDesc')}</p>
                    </div>
                  </div>
                </div>

                <div 
                  className={cn(
                    "bg-white rounded-2xl p-6 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all",
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                  )}
                  style={{ transitionDelay: isVisible ? "400ms" : "0ms", transitionDuration: "600ms" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-chathero-orange/15 flex items-center justify-center flex-shrink-0">
                      <Database className="w-6 h-6 text-chathero-orange" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-primary mb-1">{t('aiAgentPlattform.wissensbasis.productTitle')}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{t('aiAgentPlattform.wissensbasis.productDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div 
                className={cn(
                  "bg-chathero-mint/10 border border-chathero-mint/20 rounded-2xl p-5 mb-6 transition-all duration-500",
                  isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
                )}
                style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
              >
                <p className="text-sm text-primary font-medium">
                  {t('aiAgentPlattform.wissensbasis.highlight')}
                </p>
              </div>

              <Link 
                to={localizePath("/produkte/ai-knowledge-base")}
                className="inline-flex items-center gap-2 text-chathero-orange font-semibold hover:gap-3 transition-all"
              >
                {t('aiAgentPlattform.wissensbasis.linkText')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="order-2 lg:order-none">
              <KnowledgeBaseMoodVisual />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WissensbasisFundamentSection;
